'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useLabsStore, usePack } from '../pack-context';
import { computeLean } from '../scoring';
import { StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M1 · Results — the ADVISORY summary of the swipe lean, rendered through the fs-* design system
 * (fs-* tokens + DM Serif Display / Inter).
 *
 * LAYOUT (white card, centered): an uppercase eyebrow, a serif count line ("You picked N <pole A>
 * days and N <pole B> days."), a SHORT 36px-tall two-color lean bar with inline white % labels
 * (shown only when a half is wide enough to hold one), the bucket-aware interpretation copy, then the
 * muted bridge line into M2 (with its middle clause emphasised). Advisory only — it sets nothing.
 *
 * THE BAR IS THEMED BY THE AXIS, not by fixed tokens: each half takes its pole's `accent` hex
 * (`axis.poles.a` / `axis.poles.b`), which is the same accent the M2 slider gradient and the
 * archetype reveal use — so a vertical's two worlds read as the same two colours everywhere.
 *
 * Token mapping (design intent → fs-* token):
 *   - white inline labels #FAFAF7    → `fs-on-primary`
 *   - card surface #FFFFFF + #E6E4DD → `fs-surface` + `fs-border`
 *   - eyebrow / bridge mute #6B7280  → `fs-text-muted`
 */

/** A bar half only carries its inline "<pole> NN%" label when it is wide enough to hold one. */
const INLINE_LABEL_MIN_PCT = 15;
export function M1Results() {
  const $ = _c(80);
  const {
    axis,
    copy,
    m1
  } = usePack();
  const m1Answers = useLabsStore(_temp);
  const next = useLabsStore(_temp2);
  let T0;
  let lean;
  let poleA;
  let poleB;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== axis.poles.a || $[1] !== axis.poles.b || $[2] !== m1.cards || $[3] !== m1Answers) {
    lean = computeLean(m1.cards, m1Answers);
    poleA = axis.poles.a;
    poleB = axis.poles.b;
    const dayWord = _temp3;
    T0 = StepShell;
    t16 = "flex flex-col items-center gap-8";
    t14 = "w-full rounded-fs-card border border-fs-border bg-fs-surface p-7 text-center";
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <div className="mb-3 font-medium text-[11px] text-fs-text-muted uppercase tracking-[0.12em]">Your day-picks</div>;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    t0 = "mb-4 font-instrument text-[26px] text-fs-text leading-[1.15] tracking-[-0.5px]";
    t1 = "You picked ";
    t2 = <strong className="font-semibold">{lean.aCount}</strong>;
    t3 = " ";
    t4 = poleA.label;
    t5 = " ";
    t6 = dayWord(lean.aCount);
    t7 = " and";
    t8 = " ";
    t9 = <strong className="font-semibold">{lean.bCount}</strong>;
    t10 = " ";
    t11 = poleB.label;
    t12 = " ";
    t13 = dayWord(lean.bCount);
    $[0] = axis.poles.a;
    $[1] = axis.poles.b;
    $[2] = m1.cards;
    $[3] = m1Answers;
    $[4] = T0;
    $[5] = lean;
    $[6] = poleA;
    $[7] = poleB;
    $[8] = t0;
    $[9] = t1;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[4];
    lean = $[5];
    poleA = $[6];
    poleB = $[7];
    t0 = $[8];
    t1 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t17;
  if ($[26] !== t0 || $[27] !== t1 || $[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t17 = <h1 className={t0}>{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}.</h1>;
    $[26] = t0;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = `${lean.aPct}%`;
  let t19;
  if ($[41] !== poleA.accent || $[42] !== t18) {
    t19 = {
      backgroundColor: poleA.accent,
      width: t18
    };
    $[41] = poleA.accent;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const t20 = lean.aPct > INLINE_LABEL_MIN_PCT ? `${poleA.label} ${lean.aPct}%` : "";
  let t21;
  if ($[44] !== t19 || $[45] !== t20) {
    t21 = <div className="flex items-center justify-center whitespace-nowrap font-semibold text-[12px] text-fs-on-primary leading-none" style={t19}>{t20}</div>;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = `${lean.bPct}%`;
  let t23;
  if ($[47] !== poleB.accent || $[48] !== t22) {
    t23 = {
      backgroundColor: poleB.accent,
      width: t22
    };
    $[47] = poleB.accent;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = lean.bPct > INLINE_LABEL_MIN_PCT ? `${poleB.label} ${lean.bPct}%` : "";
  let t25;
  if ($[50] !== t23 || $[51] !== t24) {
    t25 = <div className="flex items-center justify-center whitespace-nowrap font-semibold text-[12px] text-fs-on-primary leading-none" style={t23}>{t24}</div>;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t21 || $[54] !== t25) {
    t26 = <div className="my-5 flex h-9 items-stretch overflow-hidden rounded-[8px] border border-fs-border">{t21}{t25}</div>;
    $[53] = t21;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const t27 = lean.rightCount === 0 && copy.m1.results.noneChosen ? copy.m1.results.noneChosen : copy.m1.results.interpretation[lean.bucket];
  let t28;
  if ($[56] !== t27) {
    t28 = <p className="mt-5 text-fs-body-lg text-fs-text-subtle leading-relaxed">{t27}</p>;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== copy.m1.results.bridge.emphasis) {
    t29 = <strong className="font-semibold text-fs-text">{copy.m1.results.bridge.emphasis}</strong>;
    $[58] = copy.m1.results.bridge.emphasis;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== copy.m1.results.bridge.post || $[61] !== copy.m1.results.bridge.pre || $[62] !== t29) {
    t30 = <p className="mt-4 text-fs-body-md text-fs-text-muted leading-relaxed">{copy.m1.results.bridge.pre}{t29}{copy.m1.results.bridge.post}</p>;
    $[60] = copy.m1.results.bridge.post;
    $[61] = copy.m1.results.bridge.pre;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t14 || $[65] !== t15 || $[66] !== t17 || $[67] !== t26 || $[68] !== t28 || $[69] !== t30) {
    t31 = <div className={t14}>{t15}{t17}{t26}{t28}{t30}</div>;
    $[64] = t14;
    $[65] = t15;
    $[66] = t17;
    $[67] = t26;
    $[68] = t28;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== next) {
    t32 = <StepNav onNext={next} />;
    $[71] = next;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t16 || $[74] !== t31 || $[75] !== t32) {
    t33 = <div className={t16}>{t31}{t32}</div>;
    $[73] = t16;
    $[74] = t31;
    $[75] = t32;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== T0 || $[78] !== t33) {
    t34 = <T0>{t33}</T0>;
    $[77] = T0;
    $[78] = t33;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  return t34;
}
function _temp3(n) {
  return n === 1 ? "day" : "days";
}
function _temp2(s_0) {
  return s_0.next;
}
function _temp(s) {
  return s.m1Answers;
}